/**
 * Dynamic Layers Configuration
 *
 * Defines all dynamic map layers that appear based on the active section.
 * These layers visualize data already fetched and displayed in list views.
 */

import { PRICING_COLORS } from '../utils/pricingLayerHelpers'
import { NOISE_COLORS } from '../utils/noiseLayerHelpers'

export interface DynamicLayerConfig {
  /** Unique layer ID */
  id: string
  /** Display name in layer panel */
  name: string
  /** Description/tooltip for the layer */
  description?: string
  /** Whether layer is enabled by default */
  defaultEnabled: boolean
  /** Whether this layer can be toggled */
  toggleable: boolean
  /** Whether this layer has an opacity slider */
  hasOpacity: boolean
  /** Default opacity (0-100) */
  defaultOpacity?: number
  /** Layer type */
  type: 'markers' | 'heatmap' | 'circle' | 'polygon' | 'line'
  /** Exclusive group ID - only one layer in the group can be active */
  exclusiveGroup?: string
  /** Whether to show a legend for this layer */
  showLegend?: boolean
  /** Rendering configuration */
  render?: {
    /** Cluster markers at low zoom levels */
    clustering?: boolean
    /** Minimum zoom level for clustering */
    clusterMinZoom?: number
    /** Maximum zoom level for clustering */
    clusterMaxZoom?: number
    /** Custom icon name */
    icon?: string
    /** Color scheme */
    colors?: string[]
    /** Fill opacity for polygons */
    fillOpacity?: number
    /** Stroke color for lines and polygons */
    strokeColor?: string
    /** Stroke width for lines and polygons */
    strokeWidth?: number
  }
}

/**
 * All dynamic layer definitions
 */
export const DYNAMIC_LAYERS_CONFIG: Record<string, DynamicLayerConfig> = {
  // ============================================================
  // LISTINGS LAYERS (Purchase & Rental)
  // ============================================================

  'listings-markers': {
    id: 'listings-markers',
    name: 'Listing Markers',
    description: 'Show individual property listings on the map',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 100,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      icon: 'home',
      colors: ['#28A745', '#FFC107', '#DC3545'], // Green, Yellow, Red by price
    },
  },

  // ============================================================
  // TRANSACTIONS LAYERS
  // ============================================================

  'transaction-points': {
    id: 'transaction-points',
    name: 'Transaction Points',
    description: 'Show historical real estate transactions',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 100,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      icon: 'trending-up',
      colors: ['#9B59B6', '#3498DB', '#1ABC9C'], // Purple to green by recency
    },
  },

  // ============================================================
  // BUILDING PERMITS LAYERS
  // ============================================================

  'building-permits-markers': {
    id: 'building-permits-markers',
    name: 'Building Permits',
    description: 'Show building permit locations on the map',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 100,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      icon: 'construction',
      colors: ['#8b5cf6'], // Purple color for building permits
    },
  },

  // ============================================================
  // AMENITIES LAYERS
  // ============================================================

  'amenities-shopping': {
    id: 'amenities-shopping',
    name: 'Shopping',
    description: 'Shopping centers, malls, retail stores',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#ff2d55'],
    },
  },

  'amenities-grocery': {
    id: 'amenities-grocery',
    name: 'Grocery',
    description: 'Supermarkets, grocery stores',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#00b341'],
    },
  },

  'amenities-culture': {
    id: 'amenities-culture',
    name: 'Culture',
    description: 'Museums, theaters, cultural venues',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#af52de'],
    },
  },

  'amenities-dining': {
    id: 'amenities-dining',
    name: 'Dining',
    description: 'Restaurants, cafes, bars',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#ff3b30'],
    },
  },

  'amenities-education': {
    id: 'amenities-education',
    name: 'Education',
    description: 'Schools, universities, libraries',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#ff9500'],
    },
  },

  'amenities-wellness': {
    id: 'amenities-wellness',
    name: 'Wellness',
    description: 'Gyms, spas, wellness centers',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#ff69b4'],
    },
  },

  'amenities-healthcare': {
    id: 'amenities-healthcare',
    name: 'Healthcare',
    description: 'Hospitals, clinics, pharmacies',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#7b61ff'],
    },
  },

  'amenities-transport': {
    id: 'amenities-transport',
    name: 'Transport',
    description: 'Public transport stops and stations',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#00ade6'],
    },
  },

  'amenities-services': {
    id: 'amenities-services',
    name: 'Services',
    description: 'Banks, post offices, public services',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMinZoom: 0,
      clusterMaxZoom: 14,
      colors: ['#8e8e93'],
    },
  },

  // ============================================================
  // TRANSPORT & ACCESS LAYERS
  // ============================================================

  // Transport Stop Markers (3 layers - independent toggles)
  'transport-bus-stops': {
    id: 'transport-bus-stops',
    name: 'Bus Stops',
    description: 'Public bus stops within 1.5km radius',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMaxZoom: 14,
      colors: ['#f59e0b'], // Orange
    },
  },

  'transport-tram-stops': {
    id: 'transport-tram-stops',
    name: 'Tram Stops',
    description: 'Tram stops within 1.5km radius',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMaxZoom: 14,
      colors: ['#3b82f6'], // Blue
    },
  },

  'transport-train-stops': {
    id: 'transport-train-stops',
    name: 'Train Stations',
    description: 'Train stations within 1.5km radius',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMaxZoom: 14,
      colors: ['#ef4444'], // Red
    },
  },

  // Travel Time Isochrones (3 layers - mutually exclusive)
  'transport-walking-isochrones': {
    id: 'transport-walking-isochrones',
    name: 'Walking Travel Time',
    description: '5, 10, 15, 30 minute walking zones',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'transport-isochrones', // Mutually exclusive
    render: {
      colors: ['#22c55e', '#eab308', '#f97316', '#ef4444'], // 5→10→15→30 min (green→red)
      fillOpacity: 0.3,
    },
  },

  'transport-cycling-isochrones': {
    id: 'transport-cycling-isochrones',
    name: 'Cycling Travel Time',
    description: '5, 10, 15, 30 minute cycling zones',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'transport-isochrones', // Mutually exclusive
    render: {
      colors: ['#22c55e', '#eab308', '#f97316', '#ef4444'], // 5→10→15→30 min (green→red)
      fillOpacity: 0.3,
    },
  },

  'transport-driving-isochrones': {
    id: 'transport-driving-isochrones',
    name: 'Driving Travel Time',
    description: '5, 10, 15, 30 minute driving zones',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'transport-isochrones', // Mutually exclusive
    render: {
      colors: ['#22c55e', '#eab308', '#f97316', '#ef4444'], // 5→10→15→30 min (green→red)
      fillOpacity: 0.3,
    },
  },

  // Additional Infrastructure (2 layers)
  'transport-ev-charging': {
    id: 'transport-ev-charging',
    name: 'EV Charging Stations',
    description: 'Electric vehicle charging stations within 5km',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMaxZoom: 14,
      colors: ['#8b5cf6'], // Purple
    },
  },

  'transport-highway-access': {
    id: 'transport-highway-access',
    name: 'Highway Access',
    description: 'Highway exits and entrances within 15km',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'markers',
    render: {
      clustering: true,
      clusterMaxZoom: 14,
      colors: ['#10b981'], // Green
    },
  },

  // ============================================================
  // DEMOGRAPHICS LAYERS
  // ============================================================

  'demographics-isochrone': {
    id: 'demographics-isochrone',
    name: 'Search Area',
    description: 'Shows the travel time area used for demographic analysis',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    render: {
      colors: ['#3b82f6'], // Blue for demographics isochrone
      fillOpacity: 0.3,
      strokeColor: '#2563eb',
      strokeWidth: 2,
    },
  },

  'demographics-population-density': {
    id: 'demographics-population-density',
    name: 'Population Density',
    description: 'Core urbanization indicator for real estate demand',
    defaultEnabled: true, // Only this one enabled by default
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: [
        '#FFFFCC',
        '#FFEDA0',
        '#FED976',
        '#FD8D3C',
        '#E31A1C',
        '#B10026',
      ],
      fillOpacity: 0.3,
    },
  },

  'demographics-foreign-pct': {
    id: 'demographics-foreign-pct',
    name: 'Foreign Population',
    description: 'International market demand indicator',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#EFF3FF', '#BDD7E7', '#6BAED6', '#3182BD', '#08519C'],
      fillOpacity: 0.3,
    },
  },

  'demographics-young-adult-pct': {
    id: 'demographics-young-adult-pct',
    name: 'Young Adults (20-39)',
    description: 'Rental market strength and starter home demand',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#EDFDE7', '#BAE4B3', '#74C476', '#31A354', '#006D2C'],
      fillOpacity: 0.3,
    },
  },

  'demographics-elderly-pct': {
    id: 'demographics-elderly-pct',
    name: 'Elderly (65+)',
    description: 'Accessible housing and healthcare proximity demand',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#F2F0F7', '#CBC9E2', '#9E9AC8', '#756BB1', '#54278F'],
      fillOpacity: 0.3,
    },
  },

  'demographics-growth-rate': {
    id: 'demographics-growth-rate',
    name: 'Population Growth (5-year)',
    description: 'Market momentum and investment potential',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#D7191C', '#FDAE61', '#FFFFBF', '#A6D96A', '#1A9641'],
      fillOpacity: 0.3,
    },
  },

  'demographics-age-group': {
    id: 'demographics-age-group',
    name: 'Dominant Age Group',
    description: 'Dominant demographic segment for targeted marketing',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#4DAF4A', '#377EB8', '#E41A1C', '#984EA3'],
      fillOpacity: 0.3,
    },
  },

  'demographics-household-type': {
    id: 'demographics-household-type',
    name: 'Dominant Household Type',
    description: 'Housing unit size demand (studios vs family homes)',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#FF7F00', '#FFFF33', '#A65628', '#F781BF', '#999999'],
      fillOpacity: 0.3,
    },
  },

  'demographics-residence-duration': {
    id: 'demographics-residence-duration',
    name: 'Dominant Residence Duration',
    description: 'Neighborhood stability and turnover indicator',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'demographics-choropleth', // Mutually exclusive
    render: {
      colors: ['#FEE5D9', '#FCAE91', '#FB6A4A', '#CB181D'],
      fillOpacity: 0.3,
    },
  },

  // ============================================================
  // EMPLOYMENT LAYERS
  // ============================================================

  'employment-isochrone': {
    id: 'employment-isochrone',
    name: 'Search Area',
    description: 'Shows the travel time area used for employment analysis',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    render: {
      colors: ['#8b5cf6'], // Purple for employment isochrone
      fillOpacity: 0.3,
      strokeColor: '#7c3aed',
      strokeWidth: 2,
    },
  },

  'employment-density': {
    id: 'employment-density',
    name: 'Employment Density',
    description:
      'Commercial real estate demand and economic activity indicator',
    defaultEnabled: true, // Default layer for employment
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'employment-choropleth', // Mutually exclusive
    render: {
      colors: [
        '#FFFFCC',
        '#FFEDA0',
        '#FED976',
        '#FD8D3C',
        '#E31A1C',
        '#B10026',
      ],
      fillOpacity: 0.3,
    },
  },

  'employment-growth-rate': {
    id: 'employment-growth-rate',
    name: 'Employment Growth (5-year)',
    description: 'Market momentum and investment potential',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'employment-choropleth', // Mutually exclusive
    render: {
      colors: ['#D7191C', '#FDAE61', '#FFFFBF', '#A6D96A', '#1A9641'],
      fillOpacity: 0.3,
    },
  },

  'employment-fulltime-ratio': {
    id: 'employment-fulltime-ratio',
    name: 'Full-Time Employment Ratio',
    description: 'Income stability and residential demand strength',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'employment-choropleth', // Mutually exclusive
    render: {
      colors: ['#EDFDE7', '#BAE4B3', '#74C476', '#31A354', '#006D2C'],
      fillOpacity: 0.3,
    },
  },

  'employment-jobs-per-establishment': {
    id: 'employment-jobs-per-establishment',
    name: 'Jobs per Establishment',
    description: 'Business size indicator (corporate vs SME)',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'employment-choropleth', // Mutually exclusive
    render: {
      colors: [
        '#EFF3FF',
        '#BDD7E7',
        '#6BAED6',
        '#3182BD',
        '#08519C',
        '#08306B',
      ],
      fillOpacity: 0.3,
    },
  },

  'employment-dominant-sector': {
    id: 'employment-dominant-sector',
    name: 'Dominant Employment Sector',
    description: 'Economic character (Primary/Secondary/Tertiary)',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'employment-choropleth', // Mutually exclusive
    render: {
      colors: ['#4DAF4A', '#FF7F00', '#377EB8'], // Green, Orange, Blue
      fillOpacity: 0.3,
    },
  },

  'employment-public-private': {
    id: 'employment-public-private',
    name: 'Public vs Private Sector',
    description: 'Government presence and tenant stability',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'employment-choropleth', // Mutually exclusive
    render: {
      colors: ['#984EA3', '#999999', '#377EB8'], // Purple, Gray, Blue
      fillOpacity: 0.3,
    },
  },

  // ============================================================
  // PURCHASE MARKET PRICING LAYERS (H3 Hexagons)
  // ============================================================

  'purchase-market-apartment-price': {
    id: 'purchase-market-apartment-price',
    name: 'Avg Purchase Price/m² - Apartment',
    description:
      'Average purchase price per square meter for apartments in the area',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'purchase-market-choropleth', // Mutually exclusive with house layer
    showLegend: true,
    render: {
      colors: PRICING_COLORS,
      fillOpacity: 0.3,
    },
  },

  'purchase-market-house-price': {
    id: 'purchase-market-house-price',
    name: 'Avg Purchase Price/m² - House',
    description:
      'Average purchase price per square meter for houses in the area',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'purchase-market-choropleth', // Mutually exclusive with apartment layer
    showLegend: true,
    render: {
      colors: PRICING_COLORS,
      fillOpacity: 0.3,
    },
  },

  // ============================================================
  // RENTAL MARKET PRICING LAYERS (H3 Hexagons)
  // ============================================================

  'rental-market-apartment-price': {
    id: 'rental-market-apartment-price',
    name: 'Avg Rental Price/m² - Apartment',
    description:
      'Average rental price per square meter for apartments in the area',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'rental-market-choropleth', // Mutually exclusive with house layer
    showLegend: true,
    render: {
      colors: PRICING_COLORS,
      fillOpacity: 0.3,
    },
  },

  'rental-market-house-price': {
    id: 'rental-market-house-price',
    name: 'Avg Rental Price/m² - House',
    description: 'Average rental price per square meter for houses in the area',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'rental-market-choropleth', // Mutually exclusive with apartment layer
    showLegend: true,
    render: {
      colors: PRICING_COLORS,
      fillOpacity: 0.3,
    },
  },

  // ============================================================
  // NOISE ANALYSIS LAYERS (H3 Hexagons)
  // ============================================================

  'noise-road-day': {
    id: 'noise-road-day',
    name: 'Road Noise - Day',
    description: 'Road noise levels during daytime in dB',
    defaultEnabled: true, // Default selected layer
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'noise-choropleth', // Mutually exclusive with other noise layers
    showLegend: true,
    render: {
      colors: NOISE_COLORS,
      fillOpacity: 0.3,
    },
  },

  'noise-road-night': {
    id: 'noise-road-night',
    name: 'Road Noise - Night',
    description: 'Road noise levels during nighttime in dB',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'noise-choropleth', // Mutually exclusive with other noise layers
    showLegend: true,
    render: {
      colors: NOISE_COLORS,
      fillOpacity: 0.3,
    },
  },

  'noise-railway-day': {
    id: 'noise-railway-day',
    name: 'Railway Noise - Day',
    description: 'Railway noise levels during daytime in dB',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'noise-choropleth', // Mutually exclusive with other noise layers
    showLegend: true,
    render: {
      colors: NOISE_COLORS,
      fillOpacity: 0.3,
    },
  },

  'noise-railway-night': {
    id: 'noise-railway-night',
    name: 'Railway Noise - Night',
    description: 'Railway noise levels during nighttime in dB',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'noise-choropleth', // Mutually exclusive with other noise layers
    showLegend: true,
    render: {
      colors: NOISE_COLORS,
      fillOpacity: 0.3,
    },
  },

  // ============================================================
  // RESTRICTIONS LAYERS (RDPPF Polygons)
  // ============================================================

  'restrictions-base': {
    id: 'restrictions-base',
    name: 'Restrictions',
    description: 'Land use restrictions and constraints',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    showLegend: false,
    fillColor: '#9333ea',
    fillOpacity: 0.3,
    strokeColor: '#7e22ce',
    strokeWidth: 2,
  } as any,

  // ============================================================
  // ZONING LAYERS (Vector Tiles with Type Filtering)
  // ============================================================

  // Zoning Type Filter (Exclusive Group)
  'zoning-all': {
    id: 'zoning-all',
    name: 'All Zoning Types',
    description: 'Display all zoning types (General, Cantonal, Local, Partial)',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'zoning-type-filter',
    showLegend: false,
  },

  'zoning-general-only': {
    id: 'zoning-general-only',
    name: 'General Only',
    description: 'Display only general zoning zones',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'zoning-type-filter',
    showLegend: false,
  },

  'zoning-cantonal-only': {
    id: 'zoning-cantonal-only',
    name: 'Cantonal Only',
    description: 'Display only cantonal zoning zones',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'zoning-type-filter',
    showLegend: false,
  },

  'zoning-local-only': {
    id: 'zoning-local-only',
    name: 'Local Only',
    description: 'Display only local zoning zones',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'zoning-type-filter',
    showLegend: false,
  },

  'zoning-partial-only': {
    id: 'zoning-partial-only',
    name: 'Partial Only',
    description: 'Display only partial zoning zones',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 30,
    type: 'polygon',
    exclusiveGroup: 'zoning-type-filter',
    showLegend: false,
  },

  // Zoning Map Style (Exclusive Group)
  'zoning-map-style': {
    id: 'zoning-map-style',
    name: 'Zoning Map',
    description: 'Display zoning on standard map',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'polygon',
    exclusiveGroup: 'zoning-map-style',
    showLegend: false,
  },

  'zoning-satellite-map-style': {
    id: 'zoning-satellite-map-style',
    name: 'Satellite Zoning Map',
    description: 'Display zoning on satellite imagery',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: false,
    type: 'polygon',
    exclusiveGroup: 'zoning-map-style',
    showLegend: false,
  },

  // ============================================================
  // HISTORICAL IMAGERY LAYERS (SWISSIMAGE)
  // ============================================================

  'historical-imagery-2024': {
    id: 'historical-imagery-2024',
    name: '2024',
    description: 'Aerial imagery from 2024',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2023': {
    id: 'historical-imagery-2023',
    name: '2023',
    description: 'Aerial imagery from 2023',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2022': {
    id: 'historical-imagery-2022',
    name: '2022',
    description: 'Aerial imagery from 2022',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2021': {
    id: 'historical-imagery-2021',
    name: '2021',
    description: 'Aerial imagery from 2021',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2020': {
    id: 'historical-imagery-2020',
    name: '2020',
    description: 'Aerial imagery from 2020',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2019': {
    id: 'historical-imagery-2019',
    name: '2019',
    description: 'Aerial imagery from 2019',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2018': {
    id: 'historical-imagery-2018',
    name: '2018',
    description: 'Aerial imagery from 2018',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2017': {
    id: 'historical-imagery-2017',
    name: '2017',
    description: 'Aerial imagery from 2017',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2016': {
    id: 'historical-imagery-2016',
    name: '2016',
    description: 'Aerial imagery from 2016',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2015': {
    id: 'historical-imagery-2015',
    name: '2015',
    description: 'Aerial imagery from 2015',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2014': {
    id: 'historical-imagery-2014',
    name: '2014',
    description: 'Aerial imagery from 2014',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2013': {
    id: 'historical-imagery-2013',
    name: '2013',
    description: 'Aerial imagery from 2013',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2012': {
    id: 'historical-imagery-2012',
    name: '2012',
    description: 'Aerial imagery from 2012',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2011': {
    id: 'historical-imagery-2011',
    name: '2011',
    description: 'Aerial imagery from 2011',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2010': {
    id: 'historical-imagery-2010',
    name: '2010',
    description: 'Aerial imagery from 2010',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2009': {
    id: 'historical-imagery-2009',
    name: '2009',
    description: 'Aerial imagery from 2009',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2008': {
    id: 'historical-imagery-2008',
    name: '2008',
    description: 'Aerial imagery from 2008',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2007': {
    id: 'historical-imagery-2007',
    name: '2007',
    description: 'Aerial imagery from 2007',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2006': {
    id: 'historical-imagery-2006',
    name: '2006',
    description: 'Aerial imagery from 2006',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2005': {
    id: 'historical-imagery-2005',
    name: '2005',
    description: 'Aerial imagery from 2005',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2004': {
    id: 'historical-imagery-2004',
    name: '2004',
    description: 'Aerial imagery from 2004',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2003': {
    id: 'historical-imagery-2003',
    name: '2003',
    description: 'Aerial imagery from 2003',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2002': {
    id: 'historical-imagery-2002',
    name: '2002',
    description: 'Aerial imagery from 2002',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2001': {
    id: 'historical-imagery-2001',
    name: '2001',
    description: 'Aerial imagery from 2001',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-2000': {
    id: 'historical-imagery-2000',
    name: '2000',
    description: 'Aerial imagery from 2000',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1999': {
    id: 'historical-imagery-1999',
    name: '1999',
    description: 'Aerial imagery from 1999',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1998': {
    id: 'historical-imagery-1998',
    name: '1998',
    description: 'Aerial imagery from 1998',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1997': {
    id: 'historical-imagery-1997',
    name: '1997',
    description: 'Aerial imagery from 1997',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1996': {
    id: 'historical-imagery-1996',
    name: '1996',
    description: 'Aerial imagery from 1996',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1995': {
    id: 'historical-imagery-1995',
    name: '1995',
    description: 'Aerial imagery from 1995',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1994': {
    id: 'historical-imagery-1994',
    name: '1994',
    description: 'Aerial imagery from 1994',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1993': {
    id: 'historical-imagery-1993',
    name: '1993',
    description: 'Aerial imagery from 1993',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1992': {
    id: 'historical-imagery-1992',
    name: '1992',
    description: 'Aerial imagery from 1992',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1991': {
    id: 'historical-imagery-1991',
    name: '1991',
    description: 'Aerial imagery from 1991',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  'historical-imagery-1990': {
    id: 'historical-imagery-1990',
    name: '1990',
    description: 'Aerial imagery from 1990',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 80,
    type: 'polygon',
    exclusiveGroup: 'historical-imagery',
    showLegend: false,
  },

  // ============================================================
  // ENERGY LAYERS
  // ============================================================

  'energy-solar-potential': {
    id: 'energy-solar-potential',
    name: 'Solar Potential',
    description: 'Roof sections colored by solar potential (kWh/m²/year)',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 60, // Higher opacity for better visibility on roof
    type: 'polygon',
    showLegend: true,
    render: {
      colors: ['#2DC5FF', '#FFFC09', '#FCAA00', '#FA5502', '#A80200'], // Poor to Excellent
      fillOpacity: 0.6,
    },
  },

  // ============================================================
  // SUNLIGHT STUDY SECTION
  // ============================================================

  'sunlight-shadow-simulation': {
    id: 'sunlight-shadow-simulation',
    name: 'Shadow Simulation',
    description: 'Real-time shadow simulation based on date and time',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 70,
    type: 'polygon',
    exclusiveGroup: 'sunlight-study',
    showLegend: false,
  },

  'sunlight-hours': {
    id: 'sunlight-hours',
    name: 'Sunlight Hours',
    description:
      'Color-coded visualization showing total hours of sun exposure per day from sunrise to sunset',
    defaultEnabled: false,
    toggleable: true,
    hasOpacity: true,
    defaultOpacity: 70,
    type: 'polygon',
    exclusiveGroup: 'sunlight-study',
    showLegend: false,
  },

  'sun-direction': {
    id: 'sun-direction',
    name: 'Sun Direction',
    description:
      'Shows sunrise, sunset, and current sun direction as lines from map center',
    defaultEnabled: true,
    toggleable: true,
    hasOpacity: false,
    type: 'line',
    exclusiveGroup: null,
    showLegend: true,
  },
}

/**
 * Get layer configuration by ID
 */
export function getLayerConfig(
  layerId: string
): DynamicLayerConfig | undefined {
  return DYNAMIC_LAYERS_CONFIG[layerId]
}

/**
 * Get multiple layer configurations
 */
export function getLayerConfigs(layerIds: string[]): DynamicLayerConfig[] {
  return layerIds.map((id) => DYNAMIC_LAYERS_CONFIG[id]).filter(Boolean)
}
